<template>
    <v-container fluid class="pa-0">
        <v-row no-gutters align="center" justify="end" class="flex-nowrap">
            <v-col cols="auto">
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            icon
                            x-small
                            v-on="on"
                            @click="onViewMetadataClick"
                            class="mx-1"
                        >
                            <v-icon small :color="metadataIconColor">mdi-text-box-search</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t("Site.TableView.ViewMetadata") }}</span>
                </v-tooltip>
            </v-col>

            <v-col cols="auto">
                <v-menu
                    bottom
                    offset-y
                    :left="alignActionsMenuLeft"
                    :right="!alignActionsMenuLeft"
                    v-model="viewingActionsMenu"
                    :close-on-content-click="false"
                    max-width="400"
                >
                    <template v-slot:activator="{ on: onMenu, attrs }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on: onTooltip, attrs: tooltipAttrs }">
                                <v-btn
                                    icon
                                    x-small
                                    v-bind="{ ...attrs, ...tooltipAttrs }"
                                    v-on="{ ...onMenu, ...onTooltip }"
                                    class="mx-1"
                                >
                                    <v-icon small>mdi-dots-horizontal</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t("Actions") }}</span>
                        </v-tooltip>
                    </template>
                    <v-list dense>
                        <v-list-item>
                            <DocumentItemToolbar
                                :v-if="!document.isDeleted"
                                :documentId="document.id"
                                :permissions="document.permissions"
                                :commentsCount="document.totalComments"
                                :attachmentsCount="document.totalAttachments"
                                :alertsCount="document.totalAlerts"
                                :linkedDocumentsCount="document.totalLinkedDocuments"
                                :canUnblock="document.canUnblock"
                                :isBlocked="document.isBlocked"
                                :lockInfo="document.lockInfo"
                                :isDistributed="document.isDistributed"
                                :isInWorkflow="document.isInWorkflow"
                                :isPendingConfirmation="document.isPendingConfirmation"
                                :isLastVersion="document.isLastVersion"
                                :hasPlugins="false"
                                :isPdf="document.extension.toLowerCase() == '.pdf'"
                                :tableViewEnabled="true"
                                :isDigitallySigned="document.isDigitallySigned"
                                :lockType="document.documentDeferredProcessType"
                                :currentUserHasValidatedDocument="document.currentUserHasValidatedDocument"
                                :totalValidationsCount="document.totalFlowValidations"
                                @on-version-removed="onVersionRemoved"
                                @on-document-blocked="onDocumentBlocked"
                                @on-reception-confirmed="onReceptionConfirmed"
                                @on-flow-diagram-click="onFlowDiagramClick"
                                @on-action-click="onActionClick"
                                @on-sign-click="onSignClick"
                                @on-load-plugin-click="onloadPluginClick"
                                @on-retry-process="onRetryProcess"
                            />
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-col>

            <v-col cols="auto" v-if="isActionVisible('edit')">
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            icon
                            x-small
                            v-on="on"
                            @click="editDocument"
                            class="mx-1"
                        >
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t("Edit") }}</span>
                </v-tooltip>
            </v-col>

            <v-col cols="auto" v-if="isActionVisible('delete')">
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                        icon
                        x-small
                        v-on="on"
                        type="button"
                        @click="deleteDocument"
                        class="mx-1"
                        @keydown.enter.prevent.stop
                        @keyup.enter.prevent.stop="deleteDocument"
                        >
                        <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t("Delete") }}</span>
                </v-tooltip>
            </v-col>
            <v-col cols="auto" v-if="isActionVisible('plugins')">
                <v-menu
                    bottom
                    offset-y
                    :left="alignActionsMenuLeft"
                    :right="!alignActionsMenuLeft"
                    v-model="viewingPluginsMenu"
                    :close-on-content-click="false"
                    max-width="400"
                >
                    <template v-slot:activator="{ on: onMenu, attrs }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on: onTooltip, attrs: tooltipAttrs }">
                                <v-btn
                                    icon
                                    x-small
                                    v-bind="{ ...attrs, ...tooltipAttrs }"
                                    v-on="{ ...onMenu, ...onTooltip }"
                                    @click="loadPluginList()"
                                    class="mx-1"
                                >
                                    <v-icon small>mdi-power-plug</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ $t("Plugins") }}</span>
                        </v-tooltip>
                    </template>
                    <v-list dense>
                    <v-list-item
                        v-for="plugin in pluginList"
                        v-bind:key="plugin.id"
                        @click="onloadPluginClick(plugin)"
                    >
                        <v-list-item-icon>
                            <v-icon small>{{ plugin.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>{{ plugin.name }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
                </v-menu>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import DocumentItemToolbar from '@/components/search/results/list/item/DocumentItemToolbar.vue';
import DocumentService from '@/services/DocumentService';
import PluginService from '@/services/PluginService';
import Plugin from '@/ecm/models/plugin/Plugin';
import Utils from '@/ecm/common/Utils.js';

export default {
    name: 'DocumentTableActions',
    components: {
        DocumentItemToolbar
    },
    props: {
        document: {
            type: Object,
            required: true
        },
        totalDefaultVisibleFields: {
            type: Number,
            default: 4
        },
    },
    data() {
        return {
            viewingActionsMenu: false,
            viewingPluginsMenu: false,
            expanded: false,
            pluginList: [],
        };
    },
    mounted() {
        window.addEventListener('scroll', this.closeMenuOnScroll, true);
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.closeMenuOnScroll, true);
    },
    computed: {
        metadataIconColor() {
            return this.expanded ? 'primary' : '';
        },
        alignActionsMenuLeft() {
            return this.totalDefaultVisibleFields > 2;
        }
    },
    methods: {
        closeMenuOnScroll() {
            if (this.viewingActionsMenu) {
                this.viewingActionsMenu = false;
            }
            if (this.viewingPluginsMenu) {
                this.viewingPluginsMenu = false;
            }
        },
        onViewMetadataClick() {
            this.expanded = !this.expanded;
            this.$emit('onViewMetadataClick');
        },
        onVersionRemoved(result) {
            this.$emit('on-version-removed', result);
        },
        onDocumentBlocked() {
            this.$emit('on-document-blocked');
        },
        onReceptionConfirmed() {
            this.$emit('on-reception-confirmed');
        },
        onFlowDiagramClick() {
            this.$emit('on-flow-diagram-click');
        },
        onSignClick() {
            this.$emit('on-sign-click');
        },
        onRetryProcess() {
            this.$emit('on-retry-process');
        },
        onloadPluginClick(plugin) {
            this.$emit('on-load-plugin-click', plugin);
        },
        onActionClick(action) {
            this.$emit('on-action-click', action);
        },
        editDocument() {
            this.$emit('on-edit-click');
        },
        deleteDocument() {
            const ids = [this.document.id];
            this.$confirm(this.$t('DeleteAllVersionsConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-lock-question',
                color: 'warning',
                width: 400,
                buttonTrueText: this.$t('CurrentVersion'),
                buttonFalseText: this.$t('AllVersions'),
            }).then((res) => {
                if (res > 0) {
                    const deleteAllVersions = res != 1;
                    DocumentService.removeVersion(ids, deleteAllVersions).then((result) => {
                        Utils.toast.success(this.$t('DocumentsDeletedSuccessfully'));
                        if (deleteAllVersions) {
                            this.$emit('on-document-removed', this.document.id);
                        } else {
                            this.$emit('on-version-removed', result);
                        }
                    });
                }
            });
        },
        loadPluginList() {
            PluginService.getAuthorizedList('ResultViewDocument', [
                this.document.id,
            ]).then((result) => {
                this.pluginList = result.map(function (dto) {
                    return new Plugin(dto);
                });
            });
        },
        isActionVisible(action) {
            const isActionable = !this.document.isBlocked || (this.document.isBlocked && this.document.canUnblock && this.document.documentDeferredProcessType < 2);
            switch (action) {
            case 'edit':
                return this.document.permissions.documentEdit && this.document.isLastVersion && isActionable;
            case 'delete':
                return this.document.permissions.documentDelete && isActionable;
            case 'plugins':
                return this.document.hasPlugins;
            default:
                return false;
            }
        }
    }
};
</script>